import axios from 'axios';
import getAxios from './common/header';
import CurrentUserService from './CurrentUserService';

const currentUser = new CurrentUserService();

// =============================================================================
// VendorSourceService
// =============================================================================
//
// Wraps the /api/v1/vendor-source endpoints in ACX.AnswerBank.Service.
// Backs Stage 1 of the admin AI Pre-Population wizard
// (Step 3 of Add/Edit Vendor).
//
// All methods take the target vendor's `partnerId` (NOT the admin's own
// account) so the backend can resolve the right account via getAccountData.
// `qsid` = question_set_id (numeric, env-specific — typically the SE
// question library id resolved at runtime, NOT hardcoded).
// =============================================================================

const BASE = () => process.env.VUE_APP_ANSWER_BANK_SERVICE_URL;
const COMMON_BASE = () => process.env.VUE_APP_COMMON_BASE_URL;

export default class VendorSourceService {

    // GET /vendor-source/sources?id=<partnerId>&qsid=<int>
    // Returns { urls: [...], documents: [...] } for this vendor + question set.
    async listSources(partnerId, qsid) {
        try {
            const response = await getAxios().get(
                `${BASE()}v1/vendor-source/sources?id=${partnerId}&qsid=${qsid}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.listSources', err.stack);
            return err.response;
        }
    }

    // POST /vendor-source/url?id=<partnerId>
    // Body: { qsid, url }
    async fetchUrl(partnerId, qsid, url) {
        try {
            const response = await getAxios().post(
                `${BASE()}v1/vendor-source/url?id=${partnerId}`,
                { qsid, url }
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.fetchUrl', err.stack);
            return err.response;
        }
    }

    // DELETE /vendor-source/url/:id?id=<partnerId>
    async deleteUrl(partnerId, sourceId) {
        try {
            const response = await getAxios().delete(
                `${BASE()}v1/vendor-source/url/${sourceId}?id=${partnerId}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.deleteUrl', err.stack);
            return err.response;
        }
    }

    // Document upload is a 3-step dance, matching the existing
    // /file-upload pattern in this codebase:
    //   1. GET /v1/upload-url from the common service to get a pre-signed
    //      S3 PUT URL + the resulting key
    //   2. PUT the raw file directly to S3 (no token; the pre-signed URL
    //      authorizes it)
    //   3. POST /vendor-source/document with the metadata so the platform
    //      registers + parses the file
    async uploadDocument(partnerId, qsid, file, { onProgress } = {}) {
        // 1. Pre-signed URL
        currentUser.refreshCognitoJwt && currentUser.refreshCognitoJwt();
        const nameSplit = file.name.split('.');
        const fileExtension = nameSplit[nameSplit.length - 1];
        const safeBaseName = file.name
            .split('.')[0]
            .substring(0, 70)
            .replace(/[^A-Za-z0-9._-]/g, '_');
        const signedRes = await getAxios().get(
            `${COMMON_BASE()}v1/upload-url?id=${partnerId}` +
            `&content_type=${encodeURIComponent(file.type)}` +
            `&file_name=${safeBaseName}.${fileExtension}`
        );
        const { Key: s3Key, uploadURL } = signedRes.data;

        // 2. PUT to S3
        await axios.put(uploadURL, file, {
            headers: { 'Content-Type': file.type },
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        });

        // 3. Register + parse on the backend
        const registerRes = await getAxios().post(
            `${BASE()}v1/vendor-source/document?id=${partnerId}`,
            {
                qsid,
                file_name: file.name,
                s3_key: s3Key,
                mime_type: file.type,
                file_size_bytes: file.size,
            }
        );
        return registerRes;
    }

    // DELETE /vendor-source/document/:id?id=<partnerId>
    async deleteDocument(partnerId, sourceId) {
        try {
            const response = await getAxios().delete(
                `${BASE()}v1/vendor-source/document/${sourceId}?id=${partnerId}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.deleteDocument', err.stack);
            return err.response;
        }
    }

    // =========================================================================
    // Stage 2 — coverage estimate
    // =========================================================================
    // GET /vendor-source/coverage-estimate?id=<partnerId>&qsid=<int>
    async getCoverageEstimate(partnerId, qsid) {
        try {
            const response = await getAxios().get(
                `${BASE()}v1/vendor-source/coverage-estimate?id=${partnerId}&qsid=${qsid}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.getCoverageEstimate', err.stack);
            return err.response;
        }
    }

    // =========================================================================
    // Stage 3 — run AI pre-population
    // =========================================================================
    // POST /vendor-source/run?id=<partnerId>
    // scope: 'fill_empty_only' | 'overwrite_all'
    async runIngestion(partnerId, qsid, scope, model = null) {
        try {
            const body = { qsid, scope };
            if (model) body.model = model;
            const response = await getAxios().post(
                `${BASE()}v1/vendor-source/run?id=${partnerId}`,
                body
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.runIngestion', err.stack);
            return err.response;
        }
    }

    // GET /vendor-source/runs?id=<partnerId>&qsid=<int>
    async listRuns(partnerId, qsid) {
        try {
            const response = await getAxios().get(
                `${BASE()}v1/vendor-source/runs?id=${partnerId}&qsid=${qsid}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.listRuns', err.stack);
            return err.response;
        }
    }

    // =========================================================================
    // Vendor notification email (spec section 7 — Edit / Send / Do not send)
    // =========================================================================

    // GET /vendor-source/notification-preview?id=<partnerId>&runId=<uuid>&vendorName=<name>
    async getNotificationPreview(partnerId, runId, vendorName) {
        try {
            const url =
                `${BASE()}v1/vendor-source/notification-preview` +
                `?id=${partnerId}` +
                `&runId=${runId}` +
                `&vendorName=${encodeURIComponent(vendorName || '')}`;
            const response = await getAxios().get(url);
            return response;
        } catch (err) {
            console.error('VendorSourceService.getNotificationPreview', err.stack);
            return err.response;
        }
    }

    // POST /vendor-source/notification-send?id=<partnerId>
    // Body: { runId, subject, body_html }
    async sendNotification(partnerId, runId, subject, bodyHtml) {
        try {
            const response = await getAxios().post(
                `${BASE()}v1/vendor-source/notification-send?id=${partnerId}`,
                { runId, subject, body_html: bodyHtml }
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.sendNotification', err.stack);
            return err.response;
        }
    }

    // POST /vendor-source/notification-skip?id=<partnerId>
    // Body: { runId }
    async skipNotification(partnerId, runId) {
        try {
            const response = await getAxios().post(
                `${BASE()}v1/vendor-source/notification-skip?id=${partnerId}`,
                { runId }
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.skipNotification', err.stack);
            return err.response;
        }
    }

    // POST /vendor-source/notification-finalize-new-vendor?id=<partnerId>
    // Sends the new vendor's one combined welcome email. Called once when
    // the Add/Edit Vendor wizard finishes for a new vendor -- not part of
    // the admin-reviewed Edit/Send/Skip flow above.
    async finalizeNewVendor(partnerId) {
        try {
            const response = await getAxios().post(
                `${BASE()}v1/vendor-source/notification-finalize-new-vendor?id=${partnerId}`
            );
            return response;
        } catch (err) {
            console.error('VendorSourceService.finalizeNewVendor', err.stack);
            return err.response;
        }
    }
}
